// Product "Learn more" modal data — the AI-powered "About / Common uses"
// description plus a live Q&A chat, matching the patient-dashboard shop's
// ProductInfoModal. Lets a patient in the intake flow ask questions about the
// recommended medication (e.g. "can men use this patch") without leaving the form.
//
// Backend — see docs/BACKEND_LEARN_MORE_PRODUCT_INFO_PRD.md:
//   POST ${REACT_APP_BACKEND_URL}/ai/product-info  { medId, name, visitType }
//        → { description, commonUses: [string], disclaimer? }
//   POST ${REACT_APP_BACKEND_URL}/ai/product-chat  { medId, name, question, history }
//        → { answer }
// These live under the OPEN /ai/* namespace (same unauthenticated AI-proxy pattern
// as /ai/medical-autocomplete) because the intake patient is anonymous. NOT the
// dashboard's /catalog/product-* routes — those exist but are auth-gated (401) and
// only usable from the logged-in dashboard. BE must add these two /ai/* endpoints
// (pending — 404 today; the modal soft-degrades until they ship). Perplexity key
// stays server-side. Responses are parsed defensively for BE field-name drift.
//
// Ships DARK: on only via REACT_APP_PRODUCT_INFO_ENABLED='true' (opt-in), the
// same convention as OTP / medical-autocomplete. Until then the "Learn more"
// link is hidden. Every network path soft-degrades — the modal must never break
// the checkout step it lives on.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Opt-in flag — dark by default. Flip in Vercel once the BE endpoints are
// confirmed reachable from the intake backend.
export const isProductInfoEnabled = () =>
  process.env.REACT_APP_PRODUCT_INFO_ENABLED === 'true';

const asText = (v) => (typeof v === 'string' ? v.trim() : '');

// Tolerate BE field-name drift: description may arrive under a few keys.
const pickDescription = (data) =>
  asText(data?.description) ||
  asText(data?.about) ||
  asText(data?.summary) ||
  asText(data?.text) ||
  '';

// Common uses may be an array of strings, or {label}/{text} objects.
const pickCommonUses = (data) => {
  const raw =
    data?.commonUses || data?.common_uses || data?.uses || data?.bullets || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((u) => (typeof u === 'string' ? u : asText(u?.label) || asText(u?.text)))
    .filter(Boolean);
};

const pickAnswer = (data) =>
  asText(data?.answer) ||
  asText(data?.reply) ||
  asText(data?.message) ||
  asText(data?.content) ||
  asText(data?.choices?.[0]?.message?.content) ||
  '';

// Fetch the About/Common-uses content. Resolves to a normalized shape; throws
// only so the caller can distinguish "not available" (→ soft-degrade copy) from
// a successful empty response. Never rejects on non-2xx — returns empty content.
export const fetchProductInfo = async ({ medId, name, visitType }, signal) => {
  if (!BACKEND_URL || !medId) return { description: '', commonUses: [] };
  try {
    const response = await fetch(`${BACKEND_URL}/ai/product-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({ medId, name, visitType }),
    });
    if (!response.ok) return { description: '', commonUses: [] };
    const data = await response.json();
    return {
      description: pickDescription(data),
      commonUses: pickCommonUses(data),
      disclaimer: asText(data?.disclaimer),
    };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { description: '', commonUses: [] };
  }
};

// Ask a question. Resolves to the answer string, or '' when the chat endpoint
// isn't reachable — the modal renders a soft-degrade line in that case.
export const askProductQuestion = async (
  { medId, name, question, history = [] },
  signal
) => {
  if (!BACKEND_URL || !medId || !question?.trim()) return '';
  try {
    const response = await fetch(`${BACKEND_URL}/ai/product-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        medId,
        name,
        question: question.trim(),
        history: history
          .filter((m) => m && m.role && m.content)
          .map(({ role, content }) => ({ role, content })),
      }),
    });
    if (!response.ok) return '';
    const data = await response.json();
    return pickAnswer(data);
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return '';
  }
};
